import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const CartCheckout = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart();
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    flatNo: '',
    buildingName: '',
    streetAddress: '',
    city: '',
    state: '',
    country: 'India',
    pincode: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);

  // Calculate delivery charge
  const FREE_DELIVERY_THRESHOLD = 599;
  const DELIVERY_CHARGE = 99;
  
  const getSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDiscountAmount = () => {
    return appliedCoupon ? appliedCoupon.discount_amount : 0;
  };

  const getSubtotalAfterDiscount = () => {
    return getSubtotal() - getDiscountAmount();
  };

  const getDeliveryCharge = () => {
    const subtotalAfterDiscount = getSubtotalAfterDiscount();
    return subtotalAfterDiscount >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_CHARGE;
  };

  const getFinalTotal = () => {
    return getSubtotalAfterDiscount() + getDeliveryCharge();
  };

  // Fetch available coupons
  const fetchAvailableCoupons = useCallback(async () => {
    setIsLoadingCoupons(true);
    try {
      const response = await axios.get(`${API}/coupons/public`);
      setAvailableCoupons(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load coupons:', error);
      }
      // Coupons are optional, continue without them
    } finally {
      setIsLoadingCoupons(false);
    }
  }, []); // API is a constant, axios/setIsLoadingCoupons/setAvailableCoupons are stable

  useEffect(() => {
    fetchAvailableCoupons();
  }, [fetchAvailableCoupons]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponError('');
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.toUpperCase(),
        order_total: getSubtotal()
      });

      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setCouponError('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid coupon code';
      setCouponError(errorMsg);
      setAppliedCoupon(null);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  const handleCheckout = async () => {
    // Validate cart
    if (cartItems.length === 0) {
      alert('⚠️ Your cart is empty! Please add items before checkout.');
      navigate('/');
      return;
    }

    // Collect missing fields with better formatting
    const missing = [];
    if (!customerDetails.name?.trim()) missing.push('• Full Name');
    if (!customerDetails.email?.trim()) missing.push('• Email Address');
    if (!customerDetails.phone?.trim()) missing.push('• Phone Number');
    if (!customerDetails.flatNo?.trim()) missing.push('• Flat/Unit Number');
    if (!customerDetails.streetAddress?.trim()) missing.push('• Street Address');
    if (!customerDetails.city?.trim()) missing.push('• City');
    if (!customerDetails.state?.trim()) missing.push('• State');
    if (!customerDetails.pincode?.trim()) missing.push('• Pincode');

    if (missing.length > 0) {
      alert(`⚠️ Required Fields Missing:\n\n${missing.join('\n')}\n\nPlease fill all fields marked with (*) before proceeding.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate pincode (6 digits)
    if (!/^\d{6}$/.test(customerDetails.pincode)) {
      alert('⚠️ Invalid Pincode\n\nPlease enter a valid 6-digit pincode (e.g., 400001)');
      return;
    }

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email)) {
      alert('⚠️ Invalid Email\n\nPlease enter a valid email address');
      return;
    }

    // Check Razorpay SDK
    if (!window.Razorpay) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Razorpay SDK not loaded');
      }
      alert('⚠️ Payment Gateway Error\n\nPayment system not loaded. Please refresh the page and try again.');
      return;
    }

    setIsProcessing(true);

    try {
      const subtotal = getSubtotal();
      const discountAmount = getDiscountAmount();
      const fullAddress = `${customerDetails.flatNo}, ${customerDetails.buildingName ? customerDetails.buildingName + ', ' : ''}${customerDetails.streetAddress}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country} - ${customerDetails.pincode}`;
      
      // Create order with new format expected by backend
      const orderResponse = await axios.post(`${API}/create-order`, {
        amount: getFinalTotal() * 100, // Will be recalculated by backend
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: {
          items: cartItems.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price
          })),
          subtotal: subtotal,
          discount_amount: discountAmount,
          coupon_code: appliedCoupon ? appliedCoupon.code : null,
          customer_details: {
            ...customerDetails,
            address: fullAddress
          }
        }
      });

      const { order_id, amount, currency } = orderResponse.data;

      // Initialize Razorpay
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'Roastey Snacks',
        description: 'Healthy Protein Snacks',
        order_id: order_id,
        prefill: {
          name: customerDetails.name,
          email: customerDetails.email,
          contact: customerDetails.phone
        },
        theme: {
          color: '#f97316'
        },
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            clearCart();
            navigate('/order-success', {
              state: {
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id
              }
            });
          } catch (error) {
            alert('Payment verification failed');
          }
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Payment initiation error:', error);
      }
      alert(`Failed to initiate payment: ${error.message || 'Please try again.'}`);
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-20 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-gray-300" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Fredoka, sans-serif'}}>
            Your Cart is Empty
          </h2>
          <p className="text-gray-600 mb-8">Add some delicious snacks to get started!</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600 text-white">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-8" style={{fontFamily: 'Fredoka, sans-serif'}}>
          Shopping Cart & Checkout
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="w-24 h-24 object-contain rounded-lg bg-white"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900">{item.name}</h3>
                      <p className="text-orange-500 font-bold">₹{item.price}</p>
                      
                      <div className="flex items-center gap-3 mt-3">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-semibold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="ml-auto text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl text-gray-900">
                        ₹{item.price * item.quantity}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Checkout Form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-6">
                <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Fredoka, sans-serif'}}>
                  Order Summary
                </h2>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={customerDetails.name}
                      onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerDetails.email}
                      onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerDetails.phone}
                      onChange={(e) => setCustomerDetails({...customerDetails, phone: e.target.value})}
                      placeholder="10-digit mobile number"
                      required
                    />
                  </div>

                  {/* Address Section */}
                  <div className="pt-2">
                    <h3 className="font-semibold text-gray-900 mb-3">Delivery Address *</h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="flatNo" className="text-xs">Flat/Unit No. *</Label>
                          <Input
                            id="flatNo"
                            value={customerDetails.flatNo}
                            onChange={(e) => setCustomerDetails({...customerDetails, flatNo: e.target.value})}
                            placeholder="101"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="buildingName" className="text-xs">Building Name</Label>
                          <Input
                            id="buildingName"
                            value={customerDetails.buildingName}
                            onChange={(e) => setCustomerDetails({...customerDetails, buildingName: e.target.value})}
                            placeholder="Tower A"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="streetAddress" className="text-xs">Street Address *</Label>
                        <Input
                          id="streetAddress"
                          value={customerDetails.streetAddress}
                          onChange={(e) => setCustomerDetails({...customerDetails, streetAddress: e.target.value})}
                          placeholder="Street name, landmark"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="city" className="text-xs">City *</Label>
                          <Input
                            id="city"
                            value={customerDetails.city}
                            onChange={(e) => setCustomerDetails({...customerDetails, city: e.target.value})}
                            placeholder="Mumbai"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="state" className="text-xs">State *</Label>
                          <Input
                            id="state"
                            value={customerDetails.state}
                            onChange={(e) => setCustomerDetails({...customerDetails, state: e.target.value})}
                            placeholder="Maharashtra"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="pincode" className="text-xs">Pincode *</Label>
                          <Input
                            id="pincode"
                            value={customerDetails.pincode}
                            onChange={(e) => setCustomerDetails({...customerDetails, pincode: e.target.value})}
                            placeholder="400001"
                            maxLength="6"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="country" className="text-xs">Country *</Label>
                          <Input
                            id="country"
                            value={customerDetails.country}
                            onChange={(e) => setCustomerDetails({...customerDetails, country: e.target.value})}
                            placeholder="India"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coupon Code Section */}
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-gray-900 mb-3">Apply Discount Code</h3>
                    
                    {/* Available Coupons Display */}
                    {availableCoupons.length > 0 && !appliedCoupon && (
                      <div className="mb-3 space-y-2">
                        <p className="text-xs text-gray-600">Available Offers:</p>
                        <div className="space-y-1">
                          {availableCoupons.map((coupon) => {
                            const canApply = getSubtotal() >= coupon.min_order_value;
                            return (
                              <div 
                                key={coupon.id} 
                                className={`text-xs p-2 rounded border ${canApply ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <span className="font-bold text-orange-600">{coupon.code}</span>
                                    <span className="text-gray-700 ml-2">
                                      {coupon.discount_type === 'percentage' 
                                        ? `${coupon.discount_value}% OFF` 
                                        : `₹${coupon.discount_value} OFF`}
                                    </span>
                                    {coupon.min_order_value > 0 && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Min order: ₹{coupon.min_order_value}
                                        {!canApply && ` (Add ₹${coupon.min_order_value - getSubtotal()} more)`}
                                      </div>
                                    )}
                                  </div>
                                  {canApply && (
                                    <button
                                      onClick={() => {
                                        setCouponCode(coupon.code);
                                        // Apply coupon directly
                                        axios.post(`${API}/coupons/validate`, {
                                          code: coupon.code,
                                          order_total: getSubtotal()
                                        }).then(response => {
                                          if (response.data.valid) {
                                            setAppliedCoupon(response.data);
                                            setCouponError('');
                                          }
                                        }).catch(error => {
                                          const errorMsg = error.response?.data?.detail || 'Invalid coupon code';
                                          setCouponError(errorMsg);
                                        });
                                      }}
                                      className="text-xs text-orange-600 font-semibold hover:text-orange-700"
                                    >
                                      Apply
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {appliedCoupon ? (
                      <div className="bg-green-50 border border-green-200 rounded p-3 flex justify-between items-center">
                        <div>
                          <p className="text-sm font-semibold text-green-700">
                            {appliedCoupon.code} Applied!
                          </p>
                          <p className="text-xs text-green-600">
                            You saved ₹{appliedCoupon.discount_amount}
                          </p>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-red-600 hover:text-red-700 text-sm font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex gap-2">
                          <Input
                            value={couponCode}
                            onChange={(e) => {
                              setCouponCode(e.target.value.toUpperCase());
                              setCouponError('');
                            }}
                            placeholder="Enter coupon code"
                            className="flex-1"
                          />
                          <Button
                            onClick={applyCoupon}
                            variant="outline"
                            className="border-orange-500 text-orange-600 hover:bg-orange-50"
                          >
                            Apply
                          </Button>
                        </div>
                        {couponError && (
                          <p className="text-xs text-red-600 mt-1">{couponError}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>₹{getSubtotal()}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({appliedCoupon.code})</span>
                      <span>- ₹{getDiscountAmount()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    {getDeliveryCharge() === 0 ? (
                      <span className="text-green-600 font-semibold">FREE</span>
                    ) : (
                      <span>₹{getDeliveryCharge()}</span>
                    )}
                  </div>
                  {getSubtotalAfterDiscount() < FREE_DELIVERY_THRESHOLD && (
                    <div className="text-xs text-orange-600">
                      Add ₹{FREE_DELIVERY_THRESHOLD - getSubtotalAfterDiscount()} more for free delivery!
                    </div>
                  )}
                  <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t">
                    <span>Total</span>
                    <span>₹{getFinalTotal()}</span>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={isProcessing}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-bold"
                >
                  {isProcessing ? 'Processing...' : 'Proceed to Payment'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Secure payment powered by Razorpay
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartCheckout;
